import moment from 'moment';
import jwtDecode from 'jwt-decode';
import {dateUTC} from '@/common/format/date';
import {DateTimeFormats} from '@/common/datetime-formats';
import PreferenceTypes from '@/store/preference-types';

const defaults = {
    rememberMe: false,
    impersonate: false,
    token: null,
    expiredTime: null,
    firstName: '',
    lastName: '',
    createdTime: null,
    decodedToken: null,
    organizationId: null,
    preferences: {
        hashes: {
            permissions: '', // the permissions object hash see data2hash helper and user-model.js
        },
        notifications: {
            // some additional personal settings about notifications
            // mute the timezone difference warning, String, `YYYY-MM-DD|timezoneName`
            // '' - by default '' === enable
            // see src/modules/layouts/components/alerts-menu/alerts-items.js
            mutedTimezoneNameDiff: '',
        },
        timezone: 'UTC',
        tabs: {},
        columns: {},
        modules: {},
        gridSort: {},
        dataExportFields: {},
    },
    profileFetchedTime: null,
    memberships: [],
};

// localstorage user data key
export const persistedStateUserKey = 'reb.user';

// the fields which could be updated after the login from the profile request
export const userVerifiableFields = ['firstName', 'lastName', 'createdTime', 'preferences', 'memberships'];

export default ({apiHandler, auth, storeJwt = false}) => ({
    namespaced: true,
    state: {...defaults},
    getters: {
        userId: (state, getters) => getters.membership && getters.membership.user.id,
        email: (state, getters) => getters.membership && getters.membership.user.email,
        permissions: (state, getters) => getters.membership && getters.membership.permissions,
        memberships: state => state.memberships,
        membership: state => (state.memberships && (state.memberships
            .find(({organization: {id}}) => id === state.organizationId)
            || state.memberships.find(({isDefault}) => isDefault)
            || state.memberships[0])),
        organizationId: state => state.organizationId,
        profileFetchedTime: state => state.profileFetchedTime,
        rememberMe: state => state.rememberMe === true,
        isImpersonating: state => state.impersonate === true,
        token: state => state.token,
        isTrackable: state => state.impersonate === false || state.decodedToken === null,
        isGuest: state => state.decodedToken === null,
        expiredTime: state => state.expiredTime,
        isSessionExpired: (state, getters) => {
            if (getters.expiredTime) {
                return moment.utc()
                    .isAfter(moment.utc(getters.expiredTime));
            }
            return true;
        },
        firstName: state => state.firstName,
        lastName: state => state.lastName,
        fullName: state => `${state.firstName} ${state.lastName}`.trim(),
        initials: (state) => {
            if (!state.firstName || !state.lastName) {
                return '';
            }
            return `${state.firstName[0] || ''}${state.lastName[0] || ''}`;
        },
        createdTime: state => state.createdTime,
        memberSince: (state) => {
            const time = state.createdTime;
            return `Member since ${dateUTC(time, DateTimeFormats.inlineDateValue)}`;
        },
        decodedToken: state => state.decodedToken,
        isMgsMember: (state) => {
            const mgsMerchants = [
                'ITSM',
                'digi',
                'betway',
                'gmgaming',
                'baytree',
                // support@rebilly.com uses this ID
                '60f605a5-32cd-4885-8b44-498c41cdeb95',
                // XY company
                'd3fbb7ce-c0fb-494b-b7f2-c8987d28f043',
                // Joe Pill
                '353d70b4-3f4a-4b28-9be3-38f425c8fbe5',
            ];
            if (state.organizationId) {
                return mgsMerchants.includes(state.organizationId);
            }
            return false;
        },
        // Sanitize only known preferences, to avoid storing extra values.
        preferences: ({preferences}) => Object
            .values(PreferenceTypes)
            .reduce((memo, key) => {
                // custom structure of the timezone settings
                const value = key === PreferenceTypes.timezone
                    ? {ui: Reflect.get(preferences, key)}
                    : Reflect.get(preferences, key);
                Reflect.set(memo, key, value);
                return memo;
            }, {}),
        // Use `get` prefix to distinguish getters w/ parameters and w/o.
        getUserPreference: state => (type, id = null) => (
            id
                ? state.preferences[type][id]
                : state.preferences[type]
        ),
        // returns computed (calculated) ui preferences
        uiPreferences: (state) => {
            const {mutedTimezoneNameDiff} = state.preferences.notifications || {};
            const [created, timezone] = mutedTimezoneNameDiff ? mutedTimezoneNameDiff.split('|') : [];

            let mutedTimezoneName = '';
            if (created && timezone) {
                try {
                    // the count of days since the client disabled the notification
                    const passedDays = moment.utc()
                        .diff(moment.utc(created), 'days');
                    if (passedDays <= 31) {
                        mutedTimezoneName = timezone;
                    }
                } catch (e) {
                    //
                }
            }

            return {
                notifications: {
                    // mute warning with timezone difference (browser/profile)
                    // the value is the timezone name (the name with disabled timezone)
                    mutedTimezoneNameDiff: mutedTimezoneName,
                },
            };
        },
    },
    mutations: {
        setUser(state, {
            rememberMe, impersonate, token, expiredTime, decodedToken, memberships, getters,
        }) {
            state.memberships = memberships;
            state.organizationId = getters.membership && getters.membership.organization.id;
            state.rememberMe = rememberMe;
            state.impersonate = impersonate;
            state.expiredTime = expiredTime;
            state.decodedToken = decodedToken;
            state.token = storeJwt || state.impersonate
                ? token
                : null;
        },
        updateUser(state, {payload, getters}) {
            userVerifiableFields
            // the preferences will updated in the `hydrateUserPreferences`
                .filter(f => f !== 'preferences')
                .forEach((key) => {
                    state[key] = payload[key];

                    if (key === 'memberships') {
                        state.organizationId = getters.membership.organization.id;
                    }
                });
        },
        reset(state) {
            Object.entries(defaults)
                .forEach(([key, value]) => {
                    state[key] = value;
                });
            // stop existing requests from completing
            // apiHandler.cancelActiveRequests('User logout');
        },
        updateUserPreference(state, {type, id, data}) {
            // Force reactivity within deep objects by reassigning the entire parent.
            state.preferences[type] = {
                ...state.preferences[type],
                [id]: data,
            };
        },
        updatePartialUserPreference(state, {type, id, data}) {
            // Force reactivity within deep objects by reassigning the entire parent.
            state.preferences[type] = {
                ...state.preferences[type],
                [id]: {...state.preferences[type][id], ...data},
            };
        },
        hydrateUserPreferences(state, payload = null) {
            if (payload !== null && Object.keys(payload).length) {
                const payloadClone = JSON.parse(JSON.stringify(payload));
                if (payloadClone.timezone) {
                    state.preferences.timezone = typeof payloadClone.timezone === 'string'
                        ? payloadClone.timezone
                        : payloadClone.timezone.ui;
                }
                // TODO add items to restore as Recomm is built
                const dashboardSegments = 'dashboard/segments';
                Object.values(PreferenceTypes)
                    .filter(v => v !== PreferenceTypes.timezone)
                    .forEach((type) => {
                        if (
                            type === PreferenceTypes.modules
                            && payloadClone[type] !== undefined
                            && payloadClone[type][dashboardSegments] !== undefined
                            && payloadClone[type][dashboardSegments].segments !== undefined
                        ) {
                            const getSegmentWithUpdatedName = (segment, id) => {
                                const update = {};
                                if (segment.name == null) {
                                    update.name = `Segment ${id}`;
                                }
                                return Object.assign({}, segment, update);
                            };
                            const {segments} = payloadClone[type][dashboardSegments];

                            payloadClone[type][dashboardSegments].segments = Object
                                .keys(segments)
                                .reduce((acc, key) => ({
                                    ...acc,
                                    [key]: getSegmentWithUpdatedName(segments[key], key),
                                }), {});
                        }

                        if (payloadClone[type]) {
                            state.preferences[type] = {...payloadClone[type]};
                        }
                    });
            }

            return true;
        },
        setCurrentOrganization(state, organizationId) {
            state.organizationId = organizationId;
        },
        setProfileFetchedTime(state, time) {
            state.profileFetchedTime = time;
        },
    },
    actions: {
        setUser({commit, getters}, payload) {
            payload.decodedToken = payload.token && jwtDecode(payload.token);
            payload.token = storeJwt || payload.impersonate
                ? payload.token
                : null;
            commit('setUser', {...payload, getters});
        },
        async updateUser({commit, getters, rootGetters}, payload) {
            commit('updateUser', {payload, getters});
            commit('hydrateUserPreferences', payload.preferences);
            await setTimeout(() => {
                auth.$loadPermissions(rootGetters['user/permissions']);
                // re-render application see subscriptions
            }, 1);
        },
        logout({commit}) {
            commit('reset');
        },
        populateUserPreferences({commit}, payload) {
            commit('hydrateUserPreferences', payload);
        },
        setCurrentOrganization({commit}, organizationId) {
            commit('setCurrentOrganization', organizationId);
        },
        async setUserPreference({commit, getters}, {type, id, data}) {
            commit('updateUserPreference', {
                type,
                id,
                data,
            });

            // Prevent profile commits if impersonating
            if (!getters.isImpersonating && !getters.isGuest) {
                await apiHandler.client.profile.update({
                    data: {preferences: getters.preferences},
                });
            }
        },
        async setPartialUserPreference({commit, getters}, {type, id, data}) {
            commit('updatePartialUserPreference', {type, id, data});

            // Prevent profile commits if impersonating
            if (!getters.isImpersonating && !getters.isGuest) {
                await apiHandler.client.profile.update({
                    data: {preferences: getters.preferences},
                });
            }
        },
        async fetchProfile({commit, dispatch}) {
            const {fields} = await apiHandler.client.profile.get();
            dispatch('updateUser', fields);
            commit('setProfileFetchedTime', moment().utc());
        },
    },
});
